import rawSamples from './data/plant_samples.json';
import { DEFAULT_ZONES, PLANT_TYPES } from '../constants';
import type { PlantCareProfile, PlantCatalogueType, PlantForm, PlantSample } from '../types';

type RawPlantSample = {
  Type: PlantCatalogueType;
  Name: string;
  'Species/Variety': string;
  'Suggested Watering Frequency (days - eg 2 represents water every 2 days)': number;
  'Suggested Location': string;
  'Fruits or Flowers in (Months)': number[];
  'Best Harvest Time (Months)': number[];
  Notes: string;
  'Care Profile': PlantCareProfile;
};

const rawList: RawPlantSample[] = Array.isArray(rawSamples)
  ? (rawSamples as RawPlantSample[])
  : ((rawSamples as { plants?: RawPlantSample[] }).plants || []);

function makeId(sample: RawPlantSample, index: number) {
  return `${sample.Type.toLowerCase()}-${sample.Name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${index + 1}`;
}

function inferCataloguePlantType(sample: RawPlantSample) {
  const value = `${sample.Name} ${sample['Species/Variety']}`.toLowerCase();
  return PLANT_TYPES.find((type) => type !== 'Other' && value.includes(type.toLowerCase())) || 'Other';
}

export const PLANT_SAMPLES: PlantSample[] = rawList.map((sample, index) => ({
  id: makeId(sample, index),
  type: sample.Type,
  name: sample.Name,
  variety: sample['Species/Variety'],
  plantType: inferCataloguePlantType(sample),
  wateringFrequencyDays: sample['Suggested Watering Frequency (days - eg 2 represents water every 2 days)'],
  suggestedLocation: sample['Suggested Location'],
  fruitOrFlowerMonths: sample['Fruits or Flowers in (Months)'],
  bestHarvestMonths: sample['Best Harvest Time (Months)'],
  notes: sample.Notes,
  careProfile: sample['Care Profile'],
}));

function recommendedAppZone(sample: PlantSample) {
  if (sample.type === 'Greenhouse') return 'Greenhouse';
  const location = sample.suggestedLocation.toLowerCase();
  if (location.includes('bathroom') || location.includes('humid')) return 'Bathroom';
  if (location.includes('windowsill') || location.includes('window')) return 'Kitchen Windowsill';
  if (location.includes('desktop') || location.includes('desk')) return 'Office Desk';
  if (sample.type === 'Lawn') return 'Pots';
  if (sample.type === 'Fruit Tree') return 'Pots';
  if (sample.type === 'Outdoor Flower') return location.includes('patio') || location.includes('container') || location.includes('pot') ? 'Pots' : 'Flower Bed';
  if (sample.type === 'Herb') return location.includes('pot') || location.includes('container') ? 'Pots' : 'Flower Bed';
  return 'Living Room';
}

export function applySampleToForm(sampleId: string, currentForm: PlantForm): PlantForm {
  const sample = PLANT_SAMPLES.find((item) => item.id === sampleId);
  if (!sample) {
    return {
      ...currentForm,
      sample_id: '',
      catalogue_type: '',
      suggested_location: '',
      fruit_or_flower_months: [],
      best_harvest_months: [],
      care_profile: null,
    };
  }

  return {
    ...currentForm,
    sample_id: sample.id,
    catalogue_type: sample.type,
    name: sample.name,
    variety: sample.variety,
    plant_type: sample.plantType,
    plant_count: currentForm.plant_count || '1',
    chloromance_zone: recommendedAppZone(sample) || DEFAULT_ZONES[0],
    watering_frequency_days: String(sample.wateringFrequencyDays),
    suggested_location: sample.suggestedLocation,
    fruit_or_flower_months: sample.fruitOrFlowerMonths,
    best_harvest_months: sample.bestHarvestMonths,
    care_profile: sample.careProfile,
    note: sample.notes,
  };
}

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatMonthList(months: number[]) {
  return months.length ? months.map((month) => MONTH_NAMES[month - 1]).filter(Boolean).join(', ') : 'Not applicable';
}
